import { supabase } from './supabase';
import { notifyDocumentRequestReady } from './notifications';

// Generic, retroactive-safe version of getOrCreateTrainingLinkToken
// (src/lib/training.js) — same race-safe find-or-create pattern, generalized
// to any staff_documents doc_type. A DB partial unique index backs this
// (staff_documents(staff_id, doc_type) WHERE status='sent') — if two
// requests land at nearly the same moment, only one insert wins; the loser's
// 23505 conflict is caught and resolved by re-reading the winner's token
// instead of erroring or creating a duplicate outstanding request.
export const getOrCreateStaffDocToken = async (staffId, docType, { docLabel, content, officeSignedBy, officeSignerRole } = {}) => {
  const { data: existingDoc } = await supabase.from('staff_documents')
    .select('sign_token').eq('staff_id', staffId).eq('doc_type', docType).eq('status', 'sent').maybeSingle();
  if (existingDoc?.sign_token) return { token: existingDoc.sign_token, reused: true };

  const token = crypto.randomUUID();
  const now = new Date().toISOString();
  const { error } = await supabase.from('staff_documents').insert([{
    staff_id: staffId, doc_type: docType, doc_label: docLabel, content: content || null,
    sign_token: token, status: 'sent', sent_at: now,
    office_signed_by: officeSignedBy || null, office_signed_at: officeSignedBy ? now : null, office_signer_role: officeSignerRole || null,
  }]);
  if (!error) return { token };
  if (error.code !== '23505') return { error };

  const { data: winner, error: readErr } = await supabase.from('staff_documents')
    .select('sign_token').eq('staff_id', staffId).eq('doc_type', docType).eq('status', 'sent').maybeSingle();
  if (readErr || !winner?.sign_token) return { error: readErr || new Error('Could not resolve document link after conflict') };
  return { token: winner.sign_token, reused: true };
};

// One call to create-or-reuse the token AND notify the staff member — the
// pairing every "Send request" button in PersonnelProfile.js's checklist
// needs. Doesn't insert an onboarding_events row itself (that table is
// onboarding-pipeline-flavored); callers that care about an audit trail can
// log separately via logOnboardingEvent (src/lib/staffCompliance.js).
export async function requestStaffDocument(staff, docType, { docLabel, content, officeSignedBy, officeSignerRole, lang } = {}) {
  const { token, error, reused } = await getOrCreateStaffDocToken(staff.id, docType, { docLabel, content, officeSignedBy, officeSignerRole });
  if (error) throw error;
  // Always send — even when reusing an existing outstanding token, a
  // deliberate click of "Send request" reads as "please resend/remind them",
  // not a silent no-op.
  await notifyDocumentRequestReady(staff.email, staff.full_name, docLabel || docType, `${window.location.origin}/hr-sign/${token}`, lang || staff.preferred_language);
  return { token, reused };
}
